import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import dsTheme from '../constants/design-system/theme.json'

import { theme } from '../constants/theme'

import withApp from '../components/withApp/withApp'
import SEO from '../components/Seo'
import PlanSection from '../components/PlanSection'
import TextLink from '../components/TextLink'
import Button from '../components/Button'
import ImageText from '../components/styles/ImageText'

// Modular system components
import Hero from '../modular-system/Hero'
import FaqSection from '../modular-system/FaqSection'
import FiftyFifty from '../modular-system/FiftyFifty'
import CoverageBlock from '../modular-system/CoverageBlock'

// Global Styled Components
import CenteredButton from '../components/styles/CenteredButton'
import FullWidthContainer from '../components/styles/FullWidthContainer'
import SiteContainer from '../components/styles/SiteContainer'
import { Header2 } from '../components/styles/FontSize'
import Spacer from '../components/styles/Spacer'

import HeroImage from '../static/images/5G-landing-page/5GHero.svg'
import Wallet from '../static/images/5G-landing-page/Wallet.svg'
import UKImage from '../static/images/5G-landing-page/UK.svg'
import Speedometer from '../static/images/5G-landing-page/Speedometer.svg'
import RocketSIM from '../static/images/5G-landing-page/RocketSIM.svg'
import { RICH_DATA_STRINGS } from '../constants/globalStrings'
import FeaturedIn from '../components/FeaturedIn'

const { colors } = dsTheme

const FifthGeneration = ({ plans: { plans } }) => (
  <Fragment>
    <SEO
      itemType={RICH_DATA_STRINGS.FAQ_ITEM_TYPE}
      title={fifthGenerationContent.seo.title}
      description={fifthGenerationContent.seo.description}
      keywords={fifthGenerationContent.seo.keywords}
    />

    <FullWidthContainer background={colors['UI/DarkGrey']}>
      <SiteContainer>
        <Hero
          heading={fifthGenerationContent.heading}
          subHeading={fifthGenerationContent.subHeading}
          subTitle={fifthGenerationContent.subTitle}
          benefits={fifthGenerationContent.benefits}
          CTA={fifthGenerationContent.heroCta}
          imageUrl={HeroImage}
          imageAlt={fifthGenerationContent.heroImageAlt}
          CTALink={fifthGenerationContent.CTALink}
        />
      </SiteContainer>
    </FullWidthContainer>

    <FullWidthContainer>
      <SiteContainer>
        <FiftyFifty imageTextSmall>
          <img src={fifthGenerationContent.heroImgSrc} alt="" />
          {fifthGenerationContent.heroContent}
        </FiftyFifty>
      </SiteContainer>
    </FullWidthContainer>
    <SiteContainer>
      <CoverageBlock hasFullCoverage />
    </SiteContainer>

    <FullWidthContainer background={colors['UI/DarkGrey']}>
      <SiteContainer>
        {fifthGenerationContent.imageTextSections.map((fiftyFifty, index) => (
          <Fragment>
            <FiftyFifty
              index={index}
              imageTextSmall
              inverse={Math.abs(index % 2) !== 1}
            >
              <img src={fiftyFifty.imageSrc} alt="" />
              <ImageText>{fiftyFifty.content}</ImageText>
            </FiftyFifty>
          </Fragment>
        ))}
      </SiteContainer>
      <SiteContainer>
        <CenteredButton>
          <Button
            href={fifthGenerationContent.CTALink}
            color={theme.colors.button.buttonColor}
          >
            {fifthGenerationContent.ourPlans}
          </Button>
        </CenteredButton>
      </SiteContainer>
    </FullWidthContainer>
    <SiteContainer
      data-module-id={plans.length > 0 ? 'plan-modules-data-sim' : null}
      data-module_name="simple-plans"
      data-module_placement="products-section"
    >
      <PlanSection plans={plans} hideDataDiscountPlans hideCoverageChecker />
    </SiteContainer>

    <FullWidthContainer background={colors['UI/DarkGrey']}>
      <SiteContainer>
        <FeaturedIn />
      </SiteContainer>
    </FullWidthContainer>

    <SiteContainer>
      <Spacer />
      <FaqSection faqAccordions={fifthGenerationContent.faqAccordions} />
      <Spacer />
    </SiteContainer>

    <Spacer />
  </Fragment>
)

const fifthGenerationContent = {
  heading: 'Superfast 5G at no extra cost',
  subHeading: '',
  subTitle: '',
  benefits: [
    'All our SIMs are 5G Ready',
    'Stream, browse and play in super-speed',
    'Enjoy more reliability with less buffering',
  ],
  heroContent: (
    <Fragment>
      <Header2>We’re coming your way</Header2>
      <p className="margin--regular kollektif-small">
        In partnership with our sister-brand Three, we’re continuing to roll out
        5G across the UK as fast as we can. To see if you’re currently in a 5G
        zone, take a look at our coverage checker.
      </p>
      <p className="margin--regular kollektif-small">
        {' '}
        <TextLink to="/coverage-checker">Check 5G coverage</TextLink>
      </p>
    </Fragment>
  ),
  heroImgSrc: UKImage,
  heroImageAlt: 'Pay as you go mobile',
  heroCta: 'View our plans',
  ourPlans: 'Take a look at our plans',
  CTALink: '#plans',
  seo: {
    itemType: 'https://schema.org/FAQPage',
    title: 'Superfast 5G at no extra cost',
    description:
      '5G will be a revolution in the speed and quality of SMARTY data.',
    keywords: ['Superfast 5G', '5G', '5G Data', '5G SIM'],
  },
  faqAccordions: [
    {
      faqId: '5GSIM',
      faqTitle: 'Can I use a 5G SIM in a 4G phone?',
      faqTitleTestId: '5G-title-SIM',
      faqContent:
        'To access our 5G network you will need a 5G enabled device. However, our 4G coverage is also fast and reliable, so you’ll still be able to stream, share and play on-the-go.',
      faqContentTestId: '5G-content',
    },
    {
      faqId: '5G-exp',
      faqTitle: 'Is a 5G SIM more expensive?',
      faqTitleTestId: '5G-exp-title',
      faqContent:
        'At SMARTY, 5G comes at no extra cost. All you need is a 5G-enabled device and you’ll have access to our 5G network. It’s as simple as that.',
      faqContentTestId: '5G-exp-content',
    },
    {
      faqId: '5G-inarea',
      faqTitle: 'Why don’t I have 5G in my area?',
      faqTitleTestId: 'inarea-5G-title',
      faqContent:
        'In partnership with our sister brand Three, we are currently working hard to roll out 5G across the UK. This means that it may not be in your area yet, but dont worry 5G is on its way and, in the meantime, you can still enjoy our fast and reliable 3G and 4G network.',
      faqContentTestId: '5G-content-inarea',
    },
    {
      faqId: '5G-specialSIM',
      faqTitle: 'Do you need a special SIM card for 5G?',
      faqTitleTestId: 'special-sim-title',
      faqContent:
        'All our SIM cards, old and new, are ready for 5G, so you won’t need to order a new one. The only thing you need is a 5G-enabled device, then you’re good to go.',
      faqContentTestId: '5G-content-special-sim',
    },
    {
      faqId: 'change-5G',
      faqTitle: 'How do I change my 4G SIM to 5G?',
      faqTitleTestId: 'change-5G-title',
      faqContent:
        'As long as you have a 5G-enabled device, you don’t need to change your SMARTY SIM to start receiving 5G. Your SIM will automatically pick up the 5G network if you are in a 5G area.',
      faqContentTestId: '5G-change-title',
    },

    {
      faqId: 'enabled-5G',
      faqTitle: 'What is a 5G-enabled device?',
      faqTitleTestId: 'enabled-title',
      faqContent: (
        <p>
          A 5G-enabled device is any product that’s capable of connecting to a
          5G network. This includes certain smartphones, tablets, broadband
          routers and WiFi dongles. Paired with a 5G SIM, these devices can
          access the superfast 5G network.{' '}
          <a
            href="http://help.smarty.co.uk/en/articles/5871549-which-devices-are-compatible-with-5g"
            target="_blank"
            rel="noopener noreferrer"
          >
            Learn more about 5G-enabled devices.
          </a>
        </p>
      ),
      faqContentTestId: 'enabled-5G-content',
    },

    {
      faqId: '5G-faster',
      faqTitle: 'Will I use my data faster with 5G?',
      faqTitleTestId: '5G-faster-title',
      faqContent: (
        <p>
          Whilst 5G doesnt consume more data than 4G, you may find yourself
          using more data due to the superfast speed and reliability of 5G for
          streaming, browsing and playing. If you do run out of data, our data
          add-ons are available.{' '}
          <a
            href="https://help.smarty.co.uk/en/articles/982374-our-add-ons"
            target="_blank"
            rel="noopener noreferrer"
          >
            Read more about add-ons.
          </a>
        </p>
      ),
      faqContentTestId: '5G-faster-content',
    },
  ],
  imageTextSections: [
    {
      imageSrc: Speedometer,
      content: (
        <Fragment>
          <Header2>Fast. Reliable. Value.</Header2>
          <p className="margin--none">
            From streaming your favourite films on the go, to enjoying
            super-smooth video calling with all your favourite people – you can
            do more with superfast 5G.
          </p>
        </Fragment>
      ),
    },
    {
      imageSrc: Wallet,
      content: (
        <Fragment>
          <Header2>5G at no extra cost</Header2>
          <p className="margin--none">
            All our great value SIMs get access to our superfast 5G network at
            no extra cost. That means our 5G SIM only deals are available for
            any 5G-enabled mobile, tablet or mobile broadband device. If you’ve
            already got a 4G SMARTY SIM you don’t need to upgrade it.
          </p>
        </Fragment>
      ),
    },
    {
      imageSrc: RocketSIM,
      content: (
        <Fragment>
          <Header2>Make the most of every moment</Header2>
          <p className="margin--none">
            With a 5G SIM card you’ll enjoy less buffering, so you can download
            an entire HD film in just 3 minutes. Play the latest games in
            razor-sharp quality and stay connected to the things that matter
            most.
          </p>
        </Fragment>
      ),
    },
  ],
}

FifthGeneration.propTypes = {
  plans: {
    plans: PropTypes.array.isRequired,
  },
}

export default withApp(FifthGeneration)
